import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Link, withRouter } from 'react-router-dom';
import _ from 'lodash';
import moment from 'moment';
import { MLTagManagerService } from 'ml-portals-services-react';
import appConfig from 'app-config';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { MetadataContent } from '@jutro/legacy/uiconfig';
import { withViewModelService } from '@xengage/gw-portals-viewmodel-react';
import { MLNotificationBlockUtil } from 'ml-portals-util-react';
import { MLAlertGroupComponent } from 'ml-components-common-react';
import PropTypes from 'prop-types';
import { PolicyService } from 'gw-capability-gateway';
import { MLNavigationStepsTrackingService } from 'ml-services-common-react';
import { MLGatewayAccountBillingDetailsService } from 'gw-capability-gateway-billing';
import { MLLocalDateUtil } from 'ml-portals-util-js';
import MLPolicySummaryDetailsComponent from '../../Components/MLPolicySummaryDetailsComponent/MLPolicySummaryDetailsComponent';
import MLAgentPolicyBillingPaymentDetailsComponent from '../../Components/MLAgentPolicyBillingPaymentDetailsComponent/MLAgentPolicyBillingPaymentDetailsComponent';
import MLPolicyBillingPremiumFeesComponent from '../../Components/MLPolicyBillingPremiumFeesComponent/MLPolicyBillingPremiumFeesComponent';
import MLPolicyBillingHistoryComponent from '../../Components/MLPolicyBillingHistoryComponent/MLPolicyBillingHistoryComponent';
import MLPolicyRecentPaymentsComponent from '../../Components/MLPolicyRecentPaymentsComponent/MLPolicyRecentPaymentsComponent';

import styles from './MLPolicyBilling.module.scss';
import metadata from './MLPolicyBilling.metadata.json5';

import { IntlContext, TranslatorContext } from '@jutro/legacy/locale';

const MLPolicyBilling = (props) => {
    const {
        policyDetailsData: {
            policyResponse: policy
        },
        match: {
            params: { policyNumber }
        },
        history
    } = props;
    const translator = useContext(TranslatorContext);

    const { authHeader } = useAuthentication();
    const intl = useContext(IntlContext);

    const [accountNumber, setAccountNumber] = useState(undefined);
    const [isCancelled, setIsCancelled] = useState(undefined);
    const initPolicyBilling = {
        policy: {},
        response: {}
    };
    const [policyBilling, setPolicyBilling] = useState(initPolicyBilling);
    const [hasPaymentInstrument, setHasPaymentInstrument] = useState();
    const policyNum = policy ? policy.policyNumber : policyNumber;
    const productCode = _.isUndefined(policy.product) ? undefined : policy.product.publicID;

    const {
        clearBlockAlerts,
        addErrorAlert,
        addWarningAlert,
        addSuccessAlert
    } = MLNotificationBlockUtil();

    const addAlert = useCallback((alertName, alertType, message) => {
        clearBlockAlerts(alertName);
        if (alertType === 'success') {
            addSuccessAlert(message, alertName);
        } else if (alertType === 'error') {
            addErrorAlert(message, alertName);
        } else if (alertType === 'warning') {
            addWarningAlert(message, alertName);
        }
    }, [addErrorAlert, addSuccessAlert, addWarningAlert, clearBlockAlerts]);

    const sendTagManagerEvent = (_label) => {
        const change = _label ? `Make Changes_${_label}` : 'UpdatePaymentOption';
        const formName = MLTagManagerService.getFormName() === MLTagManagerService.getStepName() ?
            `${MLTagManagerService.getStepName()}_${change}` :
            `${MLTagManagerService.getFormName()}_${MLTagManagerService.getStepName()}_${change}`;
        MLTagManagerService.sendFormStartEvent(formName);
    };

    const onLinkClick = useCallback((event) => {
        sendTagManagerEvent(`/billing-details`, undefined);
        event.preventDefault();
        MLNavigationStepsTrackingService.init('PolicyChange', productCode, translator);
        const { lobEndorsementURL, endorsementProducts } = appConfig;
        const redirectpath = `/billing-details`;
        if (endorsementProducts.includes(productCode)) {
            history.push(lobEndorsementURL[productCode], { policyNumber: policyNum,chosenStepPathML: redirectpath});
        }
    }, [history, policyNum, productCode, translator]);

    const MLcustomInlineNotification = ({className,...rest}) => {
        if (hasPaymentInstrument === false) {
            return <div className={className} {...rest}><strong>Action needed</strong>. Payment details for policy {policyNum} are missing or need updating.
                Please click on  <Link onClick={(evt) => onLinkClick(evt)} style={{"text-decoration":"none"}}>Update Payment Options</Link> to update your policy as soon as possible to keep it in good standing.</div>
        }
        return null;
    };

    const loadPolicyBillingData = async () => {
        const request = policy ? policy.policyNumber : policyNumber;
        const policySummaries = await PolicyService.getPolicySummariesFromPolicyNumbers([request], authHeader);
        if (policySummaries && policySummaries.length) {
            const firstSummary = policySummaries[0];
            setAccountNumber(firstSummary.accountNumber);
            setIsCancelled(firstSummary.isCancelled);
        }
        const billingSummaries = await MLGatewayAccountBillingDetailsService.getPolicyBillingSummary(request, authHeader);
        setHasPaymentInstrument(billingSummaries.hasPaymentInstrument_ML);
        setPolicyBilling({policy: policySummaries[0], response: billingSummaries});
    };

    useEffect(() => {
        loadPolicyBillingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getCurrentTermPolicies = () => {
        const policies = [];
        const periodsOrderedFromLast = _.orderBy(policy.periods, ['createTime_ML'], ['desc']);
        const flteredPeriods = periodsOrderedFromLast.find((period) => {
            const currentDate = new Date().setHours(0, 0, 0, 0);
            return currentDate >= MLLocalDateUtil.localDate2Date(period.effective_ML) && currentDate <= MLLocalDateUtil.localDate2Date(period.expiration_ML)
        }) || policy.latestPeriod;
        policies.push(flteredPeriods);
        return policies;
    };

    const isAK7DayPolicy = () => {
        if (policyBilling.policy?.product && policyBilling.policy?.address_ML) {
            return policyBilling.policy.product.publicID === 'PersonalAuto' && policyBilling.policy.termType_ML === '7Days' && policyBilling.policy.address_ML.state === 'AK';
        }
        return false;
    };

    const convertLocalDateToDate = useCallback((localDate, formatString) => {
        const parsedDate = new Date(localDate.year, localDate.month, localDate.day);
        return moment(parsedDate).format(formatString);
    }, []);

    const formatAmountString = useCallback((amount) => {
        try {
            return intl.formatNumber(amount.amount, { style: 'currency', currency: amount.currency });
        } catch (e) {
            return '';
        }
    }, [intl]);

    const overrideProps = {
        UpdatePaymentOption: {
            visible: !hasPaymentInstrument
        },
        policyBillingDetailsContainer: {
            visible: !_.isEqual(policyBilling, initPolicyBilling)
        },
        alertMsgContainer: {
            visible: policyBilling.response.pendingPayment_ML
        },
        policySummaryDetailsComponent: {
            policies: getCurrentTermPolicies(),
            dtoInfo: 'edge.capabilities.gateway.policy.dto.PolicyPeriodDTO',
            totalFees: policy.totalChanges_Ext,
            itemizedFees: policy.feesCosts_Ext,
            displayAlternativeTemplate: false
        },
        agentPolicyBillingPaymentDetailsComponent: {
            policyNumber: policyBilling?.policy?.policyNumber,
            accountNumber: accountNumber,
            productCode: _.isUndefined(policyBilling.policy?.product) ? undefined : policyBilling.policy.product.publicID,
            billingSummary: policyBilling.response,
            termNumber: policyBilling.policy?.termNumber_ML,
            canceled: isCancelled,
            hasLimitedAccess: isAK7DayPolicy(),
            convertLocalDateToDate,
            formatAmountString
        },
        policyBillingPremiumFeesComponent: {
            billingSummary: policyBilling.response,
            formatAmountString
        },
        policyBillingHistoryComponent: {
            billingSummary: policyBilling.response,
            formatAmountString
        },
        policyRecentPaymentsComponent: {
            billingSummary: policyBilling.response,
            formatAmountString,
            addAlert
        }
    };

    const resolvers = {
        resolveComponentMap: {
            MLPolicySummaryDetailsComponent,
            MLAgentPolicyBillingPaymentDetailsComponent,
            MLPolicyBillingPremiumFeesComponent,
            MLPolicyBillingHistoryComponent,
            MLPolicyRecentPaymentsComponent,
            MLcustomInlineNotification,
            MLAlertGroupComponent
        },
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            addAlert
        },
    };

    return (
        <MetadataContent
            uiProps={metadata.pageContent}
            overrideProps={overrideProps}
            {...resolvers} />
    );
};

MLPolicyBilling.propTypes = {
    policyDetailsData: PropTypes.shape({
        policyResponse: PropTypes.shape({})
    }),
    history: PropTypes.shape({
        push: PropTypes.func
    }),
    match: PropTypes.shape({
        params: PropTypes.shape({
            policyNumber: PropTypes.string
        })
    })
};

export default withRouter(withViewModelService(MLPolicyBilling));
